import {
  AttachmentInterface,
  AttachmentTypeEnum,
  AttributeNameEnum,
  ItemAttributeInterface
} from '@on-arte/core-types';

import { AuctionPublicDetails } from '@onArte/shared/interfaces';

import { ArtworkMetaSource, cleanTechniqueLabel } from './seoMeta.utils';

/**
 * Funkcja tłumacząca w wersji „pusty ciąg, gdy brak klucza".
 *
 * Serwer i przeglądarka tłumaczą inaczej: na serwerze czytamy słownik wprost
 * (brak klucza → `undefined`), w przeglądarce i18next oddaje sam klucz. Ta
 * sygnatura ujednolica oba przypadki, żeby składanie tekstów było jedno.
 */
export type TranslateOrEmpty = (key: string) => string;

/** Wymiary dzieła w centymetrach — tak są wpisywane w panelu i tak pokazuje je strona. */
export interface ArtworkDimensions {
  width: number | null;
  height: number | null;
  depth: number | null;
}

export function findAttributeValue(attributes: ItemAttributeInterface[], name: AttributeNameEnum): string | null {
  const value: string | undefined = attributes
    .find((attribute: ItemAttributeInterface): boolean => attribute.attributeName === name)
    ?.value;

  return value?.trim().length ? value.trim() : null;
}

/**
 * Atrybuty wielokrotnego wyboru przychodzą jako lista kluczy po przecinku
 * (`acrylic,mixedTechnique`). Każdy klucz ma etykietę w słowniku pod stałym
 * prefiksem — tym samym, którego używa formularz dodawania dzieła.
 */
function translateOptions(translate: TranslateOrEmpty, attribute: AttributeNameEnum, value: string | null): string | null {
  if (!value) {
    return null;
  }

  const labels: string[] = value
    .split(',')
    .map((key: string): string => translate(`onarte.common.itemsAttributes.${attribute}.options.${key.trim()}.label`))
    .filter((label: string): boolean => label.length > 0);

  return labels.length ? labels.join(', ') : null;
}

/**
 * Nazwa kategorii przychodzi z API jako klucz (`painting`, `sculpture`), nie tekst.
 */
export function translateCategory(translate: TranslateOrEmpty, categoryName: string | undefined): string | null {
  if (!categoryName) {
    return null;
  }

  const label: string = translate(`onarte.common.itemsCategories.${categoryName}`);

  return label.length ? label : null;
}

/**
 * Technika dzieła do szablonów z umowy (alt, opis, artMedium).
 *
 * Kolejność źródeł: atrybut `technique` (słownikowy, więc zawsze czysty), potem
 * `techniqueOther` (pole „jaką technikę zastosowano?"), na końcu `medium` — wolny
 * tekst, w którym bywają wypełniacze, stąd czyszczenie.
 */
export function buildTechniqueLabel(attributes: ItemAttributeInterface[], translate: TranslateOrEmpty): string | null {
  return translateOptions(translate, AttributeNameEnum.Technique, findAttributeValue(attributes, AttributeNameEnum.Technique))
    ?? cleanTechniqueLabel(findAttributeValue(attributes, AttributeNameEnum.TechniqueOther))
    ?? cleanTechniqueLabel(findAttributeValue(attributes, AttributeNameEnum.Medium));
}

/** Materiał (płótno, brąz) — w schema.org idzie do `artworkSurface`/`material`. */
export function buildMaterialLabel(attributes: ItemAttributeInterface[], translate: TranslateOrEmpty): string | null {
  return translateOptions(translate, AttributeNameEnum.Material, findAttributeValue(attributes, AttributeNameEnum.Material))
    ?? cleanTechniqueLabel(findAttributeValue(attributes, AttributeNameEnum.MaterialOther));
}

function parseCentimeters(value: string | null): number | null {
  if (!value) {
    return null;
  }

  const parsed: number = Number(value.replace(',', '.'));

  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function getArtworkDimensions(attributes: ItemAttributeInterface[]): ArtworkDimensions {
  return {
    width: parseCentimeters(findAttributeValue(attributes, AttributeNameEnum.Width)),
    height: parseCentimeters(findAttributeValue(attributes, AttributeNameEnum.Height)),
    depth: parseCentimeters(findAttributeValue(attributes, AttributeNameEnum.Depth)),
  };
}

/**
 * Zdjęcia dzieła — wyłącznie `ItemPhoto`. Załączniki obejmują też certyfikaty
 * i zdjęcia sygnatury, a te jako obraz dzieła w wynikach wyszukiwania czy
 * w mediach społecznościowych wyglądałyby jak pomyłka.
 */
export function findArtworkPhotoUrls(details: AuctionPublicDetails): string[] {
  return (details.itemDetails.attachments ?? [])
    .filter((attachment: AttachmentInterface): boolean => attachment.type === AttachmentTypeEnum.ItemPhoto)
    .map((attachment: AttachmentInterface): string => attachment.path);
}

/**
 * Jedno źródło faktów o dziele dla wszystkich szablonów z umowy (poz. 2, 6, 7, 14).
 *
 * Autor z `manufacturer`, nie z `publicProfile`: profil publiczny bywa pusty
 * (dzieła wprowadzone bez powiązanego konta artysty), a szablon tytułu wymaga
 * imienia artysty zawsze.
 */
export function buildArtworkMetaSource(details: AuctionPublicDetails, translate: TranslateOrEmpty): ArtworkMetaSource {
  const attributes: ItemAttributeInterface[] = details.itemDetails.attributes ?? [];

  return {
    itemName: details.itemDetails.name,
    artistName: details.itemDetails.manufacturer?.name ?? details.publicProfile?.name ?? 'on*arte',
    categoryLabel: translateCategory(translate, details.itemDetails.category?.name),
    techniqueLabel: buildTechniqueLabel(attributes, translate),
    year: details.itemDetails.year,
    description: details.itemDetails.description,
    photoUrl: findArtworkPhotoUrls(details)[0] ?? null,
  };
}
